import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ServiceEditModal.module.scss';
import { Field, Form, Formik } from 'formik';
import { Button } from '@mui/material';
import { salonManagementActions } from '../../../../store';
import TextField from '../../../../../Common/TextField';
import * as yup from 'yup';
import ButtonSelector from '../../../../../Common/CustomSelector/ButtonSelector';
import MultiplySelector from '../../../../../Common/CustomSelector/MultiplySelector';
import clsx from 'clsx';
import useWindowSize from '../../../../../../utils/useWindowSize';
import { getWorkers } from '../../../../store/selectors';
import { useTranslation } from 'react-i18next';
import IOSSwitch from 'Containers/Common/CustomSwitch';

/** Solo cifre + un separatore decimale (`.` o `,`), max 2 decimali. */
const sanitizePriceInput = (raw) => {
  let value = String(raw ?? '').replace(',', '.');
  value = value.replace(/[^\d.]/g, '');
  const firstDot = value.indexOf('.');
  if (firstDot !== -1) {
    value =
      value.slice(0, firstDot + 1) +
      value.slice(firstDot + 1).replace(/\./g, '');
    const [intPart, decPart = ''] = value.split('.');
    value = `${intPart}.${decPart.slice(0, 2)}`;
  }
  return value;
};

const parsePrice = (raw) => {
  if (raw === '' || raw == null) return undefined;
  const n = Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
};

const isPriceKeyAllowed = (key, currentValue) => {
  if (
    [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ].includes(key)
  ) {
    return true;
  }
  if (/^\d$/.test(key)) return true;
  if ((key === '.' || key === ',') && !String(currentValue || '').includes('.')) {
    return true;
  }
  return false;
};

export default ({ closeModal, service, isEditForm }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const workers = useSelector(getWorkers());

  const servicesTimesInMinutes = [
    { value: 15, name: t('services.serviceModal.times.15min') },
    { value: 20, name: t('services.serviceModal.times.20min') },
    { value: 25, name: t('services.serviceModal.times.25min') },
    { value: 30, name: t('services.serviceModal.times.30min') },
    { value: 35, name: t('services.serviceModal.times.35min') },
    { value: 40, name: t('services.serviceModal.times.40min') },
    { value: 45, name: t('services.serviceModal.times.45min') },
    { value: 50, name: t('services.serviceModal.times.50min') },
    { value: 55, name: t('services.serviceModal.times.55min') },
    { value: 60, name: t('services.serviceModal.times.60min') },
  ];

  const tagsOptions = [
    { value: 'barba', name: 'Barba' },
    { value: 'taglio', name: 'Taglio' },
    { value: 'completo', name: 'Completo' },
    { value: 'estetica', name: 'Estetica' },
  ];

  useEffect(() => {
    dispatch(salonManagementActions.GET_WORKERS.REQUEST());
    return () => {
      dispatch(salonManagementActions.GET_WORKERS.CLEAR());
    };
  }, [dispatch]);

  const descDurataRaw =
    isEditForm && service
      ? service.descDurata ?? service.desc_durata
      : null;
  const hasCustomAppDuration =
    descDurataRaw != null && descDurataRaw !== '' && Number(descDurataRaw) > 0;

  const validationSchema = {
    name: yup
      .string(t('services.serviceModal.validation.nameRequired'))
      .required(t('services.serviceModal.validation.nameFieldRequired')),
    cost: yup
      .number()
      .transform((_value, originalValue) => parsePrice(originalValue))
      .typeError(t('services.serviceModal.validation.priceRequired'))
      .min(4, t('services.serviceModal.validation.priceMin'))
      .required(t('services.serviceModal.validation.priceFieldRequired'))
      .test(
        'max-decimals',
        t('services.serviceModal.validation.maxDecimals'),
        (value) =>
          value === undefined ||
          value === null ||
          Number.isInteger(value) ||
          /^\d+(\.\d{1,2})?$/.test(String(value)),
      ),
    tags: yup
      .array()
      .min(1, t('services.serviceModal.validation.tagsRequired'))
      .max(1, t('services.serviceModal.validation.tagsMax')),
  };

  const initialTags = (() => {
    if (!(isEditForm && service?.tags)) return [];
    const list = Array.isArray(service.tags) ? service.tags : [service.tags];
    return list.slice(0, 1);
  })();

  const initialValues = {
    id: isEditForm ? service.id : '',
    name: isEditForm ? service.name : '',
    duration: isEditForm ? service.duration || 30 : 30,
    cost:
      isEditForm && service.cost != null && service.cost !== ''
        ? String(service.cost)
        : '',
    description: isEditForm ? service.description : '',
    tags: initialTags,
    showCustomAppDuration: hasCustomAppDuration,
    descDurata: hasCustomAppDuration
      ? Number(descDurataRaw)
      : isEditForm
        ? service.duration || 30
        : 30,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={yup.object().shape(validationSchema)}
      onSubmit={(values) => {
        const req = {
          idServizio: values.id,
          nome: values.name,
          time: values.duration,
          prezzo: parsePrice(values.cost),
          descrizione: values.description,
          tags: (values.tags || []).slice(0, 1),
          setToAll: isEditForm ? true : false,
          descDurata: values.showCustomAppDuration ? values.descDurata : null,
        };
        if (isEditForm) {
          return dispatch(salonManagementActions.EDIT_SERVICE.REQUEST(req, closeModal));
        }

        delete req.idServizio;
        req.color = '#FFB49D';
        req.onlyVisibile = 0;

        dispatch(
          salonManagementActions.CREATE_SERVICE.REQUEST(req, ({ idservezio }) =>
            dispatch(
              salonManagementActions.EDIT_SERVICE_WORKERS.REQUEST({
                idServizio: idservezio,
                activeWorkersIds: workers.map((worker) => worker.id),
                workersToRemove: [],
              }),
            ),
          ),
        );

        return closeModal();
      }}
    >
      {({ values, setFieldValue, setFieldTouched, errors, touched }) => {
        const priceError = touched.cost && errors.cost;

        return (
          <Form
            className={clsx(styles.service_modal_wrapper, {
              [styles.service_modal_wrapper_mobile]: isMobile,
            })}
          >
            <div className={styles.body}>
              <section className={styles.section}>
                <h3 className={styles.section_title}>
                  {t('services.serviceModal.sections.info')}
                </h3>
                <Field
                  name="name"
                  id="name"
                  label={t('services.serviceModal.name')}
                  component={TextField}
                  height="48px"
                  margin="0 0 14px 0"
                  placeholder={t('services.serviceModal.namePlaceholder')}
                  autoComplete="off"
                />
                <Field
                  name="description"
                  id="description"
                  label={t('services.serviceModal.description')}
                  component={TextField}
                  textarea
                  height="100px"
                  margin="0"
                  placeholder={t('services.serviceModal.descriptionPlaceholder')}
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.section_title}>
                  {t('services.serviceModal.sections.timing')}
                </h3>
                <ButtonSelector
                  value={values.duration}
                  setValue={(value) => setFieldValue('duration', value)}
                  options={servicesTimesInMinutes}
                  title={t('services.serviceModal.duration')}
                  marginLeft={0}
                  height="48px"
                  margin="0 0 12px 0"
                  width="100%"
                />
                <div className={styles.switch_row}>
                  <div className={styles.switch_copy}>
                    <span className={styles.switch_label}>
                      {t('services.serviceModal.customAppDuration')}
                    </span>
                    <span className={styles.switch_hint}>
                      {t('services.serviceModal.customAppDurationHint')}
                    </span>
                  </div>
                  <IOSSwitch
                    checked={values.showCustomAppDuration}
                    onChange={(_event, checked) => {
                      setFieldValue('showCustomAppDuration', checked);
                      if (checked) {
                        setFieldValue(
                          'descDurata',
                          values.descDurata || values.duration,
                        );
                      }
                    }}
                  />
                </div>
                {values.showCustomAppDuration ? (
                  <ButtonSelector
                    value={values.descDurata}
                    setValue={(value) => setFieldValue('descDurata', value)}
                    options={servicesTimesInMinutes}
                    title={t('services.serviceModal.appDuration')}
                    marginLeft={0}
                    height="48px"
                    margin="12px 0 0 0"
                    width="100%"
                  />
                ) : null}
              </section>

              <section className={styles.section}>
                <h3 className={styles.section_title}>
                  {t('services.serviceModal.sections.price')}
                </h3>
                <div className={styles.price_field}>
                  <label className={styles.price_label} htmlFor="cost">
                    {t('services.serviceModal.price')}
                  </label>
                  <div
                    className={clsx(styles.price_control, {
                      [styles.price_control_error]: !!priceError,
                    })}
                  >
                    <span className={styles.price_currency} aria-hidden="true">
                      €
                    </span>
                    <input
                      id="cost"
                      name="cost"
                      className={styles.price_input}
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      placeholder={t('services.serviceModal.pricePlaceholder')}
                      value={values.cost}
                      onChange={(event) =>
                        setFieldValue('cost', sanitizePriceInput(event.target.value))
                      }
                      onBlur={() => setFieldTouched('cost', true)}
                      onKeyDown={(event) => {
                        if (event.ctrlKey || event.metaKey || event.altKey) return;
                        if (!isPriceKeyAllowed(event.key, values.cost)) {
                          event.preventDefault();
                        }
                      }}
                      onPaste={(event) => {
                        event.preventDefault();
                        const text = event.clipboardData.getData('text');
                        setFieldValue('cost', sanitizePriceInput(text));
                      }}
                    />
                  </div>
                  {priceError ? (
                    <div className={styles.price_error} role="alert">
                      {priceError}
                    </div>
                  ) : (
                    <p className={styles.price_hint}>
                      {t('services.serviceModal.priceHint')}
                    </p>
                  )}
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.section_title}>
                  {t('services.serviceModal.sections.categories')}
                </h3>
                <MultiplySelector
                  options={tagsOptions}
                  value={values.tags || []}
                  setValue={(tags) => setFieldValue('tags', tags)}
                  title={t('services.serviceModal.tags')}
                  placeholder={t('services.serviceModal.tagsPlaceholder')}
                  error={touched.tags && errors.tags}
                  isMultiple={false}
                  closeOnClick
                  readOnly
                />
              </section>
            </div>

            <div className={clsx(styles.actions_container, styles.footer)}>
              <Button
                className={styles.confirm_button}
                variant="contained"
                type="submit"
              >
                {t('services.serviceModal.save')}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
